import React, { Suspense, lazy, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { DiceTest } from './DiceTest';
import { AuthGuard } from './components/auth/AuthGuard';
import { PublicWorldsPage } from './components/worlds/PublicWorldsPage';
import { useAuthStore } from './stores/authStore';
import { ThemeProvider } from './themes/ThemeProvider';
import { initConsoleCapture } from './lib/consoleCapture';

// Capture console errors/warnings for the in-app bug reporter.
initConsoleCapture();

// ?rehearsal — expose the board stores so the Playwright rehearsal runner
// (e2e/rehearsal.spec.ts) can read and reset state. Dev server only.
if (import.meta.env.DEV && window.location.search.includes('rehearsal')) {
  void Promise.all([
    import('./stores/mobStore'), import('./stores/characterStore'), import('./stores/tokenStore'),
    import('./stores/combatStore'), import('./stores/initiativeStore'), import('./stores/sessionPlanStore'),
    import('./stores/isoSpriteStore'), import('./stores/campaignMapStore'), import('./stores/chatStore'),
    import('./stores/mapStore'), import('./stores/campaignStore'), import('./stores/dmConsoleStore'),
  ]).then((m) => {
    (window as unknown as { __rehearsal: Record<string, unknown> }).__rehearsal = Object.assign({}, ...m);
  });
}

// Use ?dice-test in URL to show standalone dice tester
const showDiceTest = window.location.search.includes('dice-test');
// ?three-test — three.js (WebGPU) battle view experiment, lazy so three stays out of the main bundle.
const showThreeTest = window.location.search.includes('three-test');
const ThreeBattleTest = lazy(() => import('./three-test/ThreeBattleTest').then((m) => ({ default: m.ThreeBattleTest })));
// ?table-view&campaign=<id> — the 3D board alone, for screen-sharing (opened by the GM's pop-out button).
const showTableView = window.location.search.includes('table-view');
const TableView = lazy(() => import('./canvas/three/TableView').then((m) => ({ default: m.TableView })));

/**
 * Public, pre-login front door.
 *
 * `#worlds` renders the Worlds gallery OUTSIDE the AuthGuard so it is reachable
 * without a session (SEO / GEO front door) — but ONLY for logged-OUT visitors.
 * A LOGGED-IN visitor at `#worlds` falls through to `<AuthGuard><App/></AuthGuard>`
 * so that App's own `#worlds` branch handles it: App forwards `autoOpenIntent`
 * to WorldsPage so a post-signup handoff reopens CampaignWizard prefilled.
 *
 * Why gate on auth: the post-signup handoff has App set `window.location.hash =
 * '#worlds'` after consuming the stashed world intent. If this Root rendered
 * PublicWorldsPage unconditionally on that hashchange it would tear down
 * `<AuthGuard><App/></AuthGuard>`, lose the in-memory `worldIntent`, and never
 * reopen the wizard (the sessionStorage key is already consumed). Routing
 * logged-in users back into the app shell keeps the intent alive and the
 * prefilled wizard opens.
 *
 * We still run `authStore.initialize()` so a logged-in visitor keeps their
 * session. While auth is still loading we render the in-app shell (AuthGuard
 * shows its own loading/login UI) rather than flashing the public gallery; once
 * resolved, only a confirmed-logged-out visitor sees PublicWorldsPage.
 *
 * Mirrors the existing `showDiceTest` pre-auth branch.
 */
function Root() {
  const [hash, setHash] = useState(window.location.hash);
  const initialize = useAuthStore((s) => s.initialize);
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);

  // Keep the session alive even on the public page so logged-in visitors don't
  // get bounced to signup. AuthGuard also calls initialize(); authStore guards
  // against double-init, so calling it from both places is safe.
  useEffect(() => {
    initialize();
  }, [initialize]);

  // SPA nav into/out of `#worlds` without a reload.
  useEffect(() => {
    const onHashChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (showDiceTest) {
    return <DiceTest />;
  }

  if (showTableView) {
    return <AuthGuard><Suspense fallback={null}><TableView /></Suspense></AuthGuard>;
  }

  if (showThreeTest) {
    return <Suspense fallback={null}><ThreeBattleTest /></Suspense>;
  }

  // Public Worlds front door — only for confirmed logged-OUT visitors. Logged-in
  // visitors (and any state while auth is still resolving) fall through to the
  // app shell so App's `#worlds` branch can forward the post-signup intent and
  // reopen the wizard prefilled. See the block comment above.
  if (hash === '#worlds' && !isLoading && !user) {
    return <PublicWorldsPage />;
  }

  return (
    <AuthGuard>
      <App />
    </AuthGuard>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <Root />
    </ThemeProvider>
  </React.StrictMode>
);
